import {
  createOneSpottedNote,
  fetchSpottedNoteList,
  GraphQLClient,
  CaseType,
} from '@rapid-recovery-agency-inc/insightt-shared';
import { createAsyncThunk, createAction } from '@reduxjs/toolkit';
import {
  CREATE_SPOTTED_NOTE_ERROR_MSG,
  CREATE_SPOTTED_NOTE_THUNK_TYPE,
  REPO_HIT_VEHICLE_MODAL_TYPE,
  REPO_HIT_ON_SEARCH_TYPE,
  REPO_HIT_RESET_STATE_TYPE,
  FETCH_SPOTTED_NOTES_THUNK_TYPE,
  FETCH_SPOTTED_NOTE_ERROR_MSG,
  REPO_HIT_LIST_LOADING,
  ASSIGN_ON_CASE_TYPE,
  ASSIGN_ON_CASE_ERROR_MSG,
  EDIT_SPOTTED_NOTE_THUNK_TYPE,
  EDIT_SPOTTED_NOTE_ERROR_MSG,
  CHANGE_COLLATERAL_COLOR_TYPE,
} from './repo-hit-list-constants';
import { CreateSpottedNoteParams, FetchSpottedNotesParams } from './repo-hit-list-types';
import { client, evictRootQueryCache } from '../../shared/apollo';
import {
  deleteSpottedNoteOnCase,
  groupCountsByRHL,
  editSpottedNoteOnCase,
  updateAssigneesOnCase,
  updateModalAssignees,
  updateSpottedNoteOnCase,
} from './repo-hit-list-utils';
import { AppState } from '../../redux/store';
import { InsighttNotification } from '../../helpers/insightt-notification';
import { RdnCase } from '@rapid-recovery-agency-inc/insightt-graphql';
import { APOLLO_ROOT_QUERY_FIELD_NAMES } from '../../constants/app.constants';

const apolloClient = client as GraphQLClient;

export const setLoading = createAction<boolean>(REPO_HIT_LIST_LOADING);

export const setVehicleModal = createAction<CaseType | null>(REPO_HIT_VEHICLE_MODAL_TYPE);

export const onFilterChange = createAction(REPO_HIT_RESET_STATE_TYPE);

export const onSearch = createAction<string | null | undefined>(REPO_HIT_ON_SEARCH_TYPE);

export const fetchSpottedNotesAction = createAsyncThunk(
  FETCH_SPOTTED_NOTES_THUNK_TYPE,
  async (data: FetchSpottedNotesParams, thunkApi) => {
    try {
      const response = await fetchSpottedNoteList(apolloClient, {
        caseId: data.caseId,
        orderBy: 'desc',
      });

      if (!response) throw new Error(FETCH_SPOTTED_NOTE_ERROR_MSG);

      return thunkApi.fulfillWithValue({ spottedNotes: response });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      InsighttNotification('error', FETCH_SPOTTED_NOTE_ERROR_MSG);
      return thunkApi.rejectWithValue(FETCH_SPOTTED_NOTE_ERROR_MSG);
    }
  },
);

export const createSpottedNoteAction = createAsyncThunk(
  CREATE_SPOTTED_NOTE_THUNK_TYPE,
  async (data: CreateSpottedNoteParams, thunkApi) => {
    const state = thunkApi.getState() as AppState;
    const currentState = state.repoHitList;
    const spottedNotes = currentState.vehicleModalSpottedNotesData ?? [];

    try {
      const response = await createOneSpottedNote(client, data);
      if (!response) throw new Error(CREATE_SPOTTED_NOTE_ERROR_MSG);

      const updatedRepoHitList = updateSpottedNoteOnCase({
        caseId: data.caseId,
        currentRepoHitList: currentState.repoHitList,
        spottedNote: response,
      });

      return thunkApi.fulfillWithValue({
        updatedRepoHitList,
        vehicleModalSpottedNotesData: [response, ...spottedNotes],
      });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      InsighttNotification('error', CREATE_SPOTTED_NOTE_ERROR_MSG);
      return thunkApi.rejectWithValue(CREATE_SPOTTED_NOTE_ERROR_MSG);
    }
  },
);

type EditSpottedNoteActionData = {
  caseId: number;
  spottedNote: object;
};
export const editSpottedNoteAction = createAsyncThunk(
  EDIT_SPOTTED_NOTE_THUNK_TYPE,
  async (data: EditSpottedNoteActionData, thunkApi) => {
    const state = thunkApi.getState() as AppState;
    const currentState = state.repoHitList;
    const currentModalSpottedNotes = state.repoHitList.vehicleModalSpottedNotesData || [];

    try {
      const { updatedRepoHitList, updatedModalSpottedNotes } = editSpottedNoteOnCase({
        caseId: data.caseId,
        currentModalSpottedNotes,
        currentRepoHitList: currentState.repoHitList,
        spottedNote: data.spottedNote,
      });

      return thunkApi.fulfillWithValue({
        repoHitList: updatedRepoHitList,
        vehicleModalSpottedNotesData: updatedModalSpottedNotes,
      });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      InsighttNotification('error', EDIT_SPOTTED_NOTE_ERROR_MSG);
      return thunkApi.rejectWithValue(EDIT_SPOTTED_NOTE_ERROR_MSG);
    }
  },
);

type DeleteSpottedNoteActionData = {
  caseId: number;
  spottedNote: object;
};
export const deleteSpottedNoteAction = createAsyncThunk(
  EDIT_SPOTTED_NOTE_THUNK_TYPE,
  async (data: DeleteSpottedNoteActionData, thunkApi) => {
    const state = thunkApi.getState() as AppState;
    const currentState = state.repoHitList;
    const currentModalSpottedNotes = state.repoHitList.vehicleModalSpottedNotesData || [];
    // Data is createspottedNoteParams?

    try {
      const { updatedRepoHitList, updatedModalSpottedNotes } = deleteSpottedNoteOnCase({
        caseId: data.caseId,
        currentModalSpottedNotes,
        currentRepoHitList: currentState.repoHitList,
        spottedNote: data.spottedNote,
      });

      return thunkApi.fulfillWithValue({
        repoHitList: updatedRepoHitList,
        vehicleModalSpottedNotesData: updatedModalSpottedNotes,
      });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      InsighttNotification('error', EDIT_SPOTTED_NOTE_ERROR_MSG);
      return thunkApi.rejectWithValue(EDIT_SPOTTED_NOTE_ERROR_MSG);
    }
  },
);

export const saveAssignedUsersAction = createAsyncThunk(
  ASSIGN_ON_CASE_TYPE,
  async (
    data: {
      caseId: number;
      usersAssigned: object[];
    },
    thunkApi,
  ) => {
    const { caseId, usersAssigned } = data;
    const state = thunkApi.getState() as AppState;
    const { user: currentUser } = state.auth;
    const { repoHitList } = state.repoHitList;
    const { vehicleModalData } = state.repoHitList;

    try {
      //Updates the assignees on the case
      const updatedRHL = await updateAssigneesOnCase({
        currentRepoHitList: repoHitList,
        caseId,
        usersAssigned,
      });
      if (!updatedRHL) throw new Error(ASSIGN_ON_CASE_ERROR_MSG);

      //Update the modal in case it's open
      const updatedModal = await updateModalAssignees(vehicleModalData, usersAssigned);

      //Gets the type from the url (params) and counts the updated number of cases for that type
      const { orderType, counter } = await groupCountsByRHL(updatedRHL, currentUser);

      if (orderType === 'involuntary') {
        return thunkApi.fulfillWithValue({
          updatedRHL,
          involuntaryCount: counter,
          updatedModal,
        });
      } else if (orderType === 'voluntary') {
        return thunkApi.fulfillWithValue({
          updatedRHL,
          voluntaryCount: counter,
          updatedModal,
        });
      } else if (orderType === 'impound') {
        return thunkApi.fulfillWithValue({
          updatedRHL,
          impoundCount: counter,
          updatedModal,
        });
      } else {
        return thunkApi.fulfillWithValue({
          updatedRHL,
          updatedModal,
        });
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      InsighttNotification('error', ASSIGN_ON_CASE_ERROR_MSG);
      return thunkApi.rejectWithValue(ASSIGN_ON_CASE_ERROR_MSG);
    }
  },
);

export const updateOneColorInRHLAction = createAsyncThunk(
  CHANGE_COLLATERAL_COLOR_TYPE,
  async (
    data: {
      caseId: number;
      color: string | undefined;
    },
    thunkApi,
  ) => {
    const state = thunkApi.getState() as AppState;
    const repoHitList = state.repoHitList.repoHitList as RdnCase[];

    evictRootQueryCache(APOLLO_ROOT_QUERY_FIELD_NAMES.rDNCases);

    const updatedRHL = repoHitList.map((repoHit) => {
      if (repoHit.caseId === data.caseId) {
        return {
          ...repoHit,
          vehicleColor: data.color,
          collateral: {
            ...repoHit.collateral,
            dynamicColor: {
              color: data.color,
            },
          },
        };
      }
      return repoHit;
    });

    return thunkApi.fulfillWithValue({
      repoHitList: updatedRHL,
    });
  },
);
